import React, { useState, useEffect } from 'react';

// Hooks
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  fetchWebhooks,
  deleteWebhook,
  updateWebhookStatus,
} from '~/redux/actions/webhooks-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';

// Components
import WebhookTable from './WebhookListing';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import { Box, Typography, GlobalStyles } from '@mui/material';

// Icons
import { GeneralInviteCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const index = () => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE);
  const [initialLoad, setInitialLoad] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    successButtonName: '',
    cancelButtonName: 'No',
  });

  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    let isActive = true;

    dispatch(fetchWebhooks(pagination)).finally(() => {
      if (isActive) setInitialLoad(false);
    });

    return () => {
      isActive = false;
    };
  }, [dispatch, pagination.page, pagination.pageSize, currentMode]);

  const handleDeleteWebhook = async (webhookId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(deleteWebhook(webhookId));
      if (response.status === 200) {
        dispatch(success(response.data?.message));
        dispatch(fetchWebhooks(pagination));
        setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
      } else {
        dispatch(failure(response.data?.message));
        setConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleUpdateWebhookStatus = async (webhook) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const payload = {
        id: webhook.id,
        status: webhook.status === 'Disabled' ? 'Enabled' : 'Disabled',
      };
      const response = await dispatch(updateWebhookStatus(payload));
      if (response.status === 200) {
        dispatch(success(response.data?.message));
        dispatch(fetchWebhooks(pagination));
        setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
      } else {
        dispatch(failure(response.data?.message));
        setConfirmModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      dispatch(failure(error.response?.data?.message || 'An error occurred'));
      setConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage webhooksPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard webhooksScreenCard">
          <Box className="olcHeaderWrap">
            <Box className="webhookHeading">
              <Typography className="pageTitle">{MESSAGES.SETTINGS.WEBHOOKS.TITLE}</Typography>
            </Box>
            <Box className="pageNewBtn">
              <OlcButton
                variant="primary"
                startIcon={<img src={GeneralInviteCIcon} alt="" />}
                onClick={() => navigate('/settings/webhooks/create')}
              >
                {MESSAGES.SETTINGS.WEBHOOKS.CREATE_BUTTON_TEXT}
              </OlcButton>
            </Box>
          </Box>
          <WebhookTable
            setConfirmModal={setConfirmModal}
            updatePagination={setPagination}
            pagination={pagination}
            initialLoad={initialLoad}
            handleDeleteWebhook={handleDeleteWebhook}
            handleUpdateWebhookStatus={handleUpdateWebhookStatus}
          />
        </Box>
      </Box>
      <ConfirmModal openConfirmModal={confirmModal} />
    </>
  );
};

export default index;
